import { Immutable } from "immer";
import React, { useContext, useSyncExternalStore } from "react";

import { Context, templates } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { rememberLanguage } from "../../utils/languageCookie";

type IProps = Immutable<{
    hreflangAlternates: templates.UtilityPageTemplate["page_chrome"]["hreflang_alternates"];
}>;

const noSubscribe = () => () => {};

/**
 * Plain links to the other-language versions of the current page, so no form
 * or CSRF token stops the page being publicly cached. A click also stores the
 * choice in the language cookie: pages without a language in their URL
 * (basket, checkout, account) have no other version to link to, so there the
 * link reloads the same page and the cookie switches its language.
 */
export const SiteHeaderLanguageToggle = ({
    hreflangAlternates,
}: IProps): React.ReactNode => {
    const context = useContext(Context);
    const currentCode = context.i18n.language.code;
    const path = context.request.path;
    const pathHasLanguage = context.site_settings.lang_picker.some((entry) =>
        path.startsWith(`/${entry.language_code}/`),
    );
    // Not server-rendered: cached HTML must not carry visitor-specific query params.
    const search = useSyncExternalStore(
        noSubscribe,
        () => getWindow()?.location.search ?? "",
        () => "",
    );
    return (
        <div>
            {context.site_settings.lang_picker
                .filter(
                    // i18n.language.code is short ("en") when Django has no
                    // info for the regional code ("en-ca").
                    (entry) =>
                        entry.language_code !== currentCode &&
                        !entry.language_code.startsWith(`${currentCode}-`),
                )
                .map((entry) => (
                    <a
                        rel="alternate"
                        key={entry.language_code}
                        href={
                            hreflangAlternates.find((alt) =>
                                [
                                    entry.language_code,
                                    entry.language_code.split("-")[0],
                                ].includes(alt.hreflang),
                            )?.href ??
                            (pathHasLanguage ? entry.url : path + search)
                        }
                        hrefLang={entry.language_code}
                        lang={entry.language_code}
                        onClick={() => rememberLanguage(entry.language_code)}
                    >
                        {entry.locale_autonym}
                    </a>
                ))}
        </div>
    );
};
